// FNP-891 / FNP-893 — Platform Controls.
//
// An *operational control* is out-of-band state that changes how the running
// platform behaves, WITHOUT a deploy, at human discretion. It is deliberately
// NOT a feature flag (a flag's lifecycle ends in deletion; these are permanent
// furniture, and their resting state is "normal" forever).
//
// Every control on the platform is listed here — including the ones that are not
// wired up yet. That is the point: a control nobody can see is a control nobody
// remembers. Unwired rows render disabled with a link to the owning ticket, so
// the remaining work stays visible instead of decaying into forgotten backlog.

/**
 * Whether a control can actually be operated from this screen today.
 *
 * - `OPERATIONAL`   — wired end to end; the toggle works.
 * - `NOT_WIRED`     — the underlying control exists but cannot be flipped at
 *                     runtime yet (typically because it is a deploy-time value).
 *                     Displayed, disabled, with the owning ticket named.
 */
export enum ControlReadiness {
    OPERATIONAL = 'OPERATIONAL',
    NOT_WIRED = 'NOT_WIRED',
}

/**
 * The live position of a control.
 *
 * `UNKNOWN` is a first-class state, not a failure: for a NOT_WIRED control we
 * genuinely cannot read the position from here, and for an OPERATIONAL control
 * the read may not have resolved yet. Never render a guess as fact.
 */
export enum ControlState {
    ON = 'ON',
    OFF = 'OFF',
    UNKNOWN = 'UNKNOWN',
}

/**
 * Which direction of the switch is the platform's normal resting position.
 *
 * Recorded per control because it is the thing an operator most needs to know at
 * a glance: "is this how it usually sits, or is someone holding it off?"
 */
export enum RestingState {
    ON = 'ON',
    OFF = 'OFF',
}

/**
 * What the API returns for a wired control. Only controls with a backend entry
 * have one — display-only rows never resolve to this.
 */
export interface PlatformControlRecord {
    id: string;
    state: ControlState;
    lastChangedBy?: string | null;
    lastChangedAt?: string | null;
    flipLatencySeconds?: number | null;
}

export interface PlatformControl {
    /** Stable id — also the row key. */
    id: string;
    /** Short human name, e.g. "User registration". */
    name: string;
    /** One line: what flipping this actually does. */
    description: string;
    /** The domain/seam that enforces it, e.g. "Cognito PreSignUp". */
    enforcedAt: string;
    /** Normal resting position, so a held-off switch reads as unusual. */
    restingState: RestingState;
    readiness: ControlReadiness;
    /** Live position. UNKNOWN until read (or permanently, when NOT_WIRED). */
    state: ControlState;
    /**
     * Why this control cannot be operated here yet, and who owns closing that
     * gap. Present only when readiness is NOT_WIRED.
     */
    notWiredReason?: string;
    /** Owning ticket for the not-yet-wired work, e.g. "FNP-792". */
    owningTicket?: string;
    /** Who last changed it, when known. */
    lastChangedBy?: string;
    /** ISO timestamp of the last change, when known. */
    lastChangedAt?: string;
    /**
     * How long a flip takes to take effect, in seconds. This is a declared
     * property of the switch (the read-through cache TTL), not an implementation
     * detail — an operator flipping it needs to know how long to wait.
     */
    flipLatencySeconds?: number;
}
